/* eslint-disable max-lines */
import './CopPerformanceChart.scss'

import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Plugin, Title, Tooltip } from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'

import { CHART_TYPE, CSS_CLASSES, DONUT_CHART_CONFIG, LAYOUT_CONFIG } from './config/chartConfig'
import { COLORS, getColorsForData } from './config/chartTheme'
import { useMultipleChartData } from './hooks/useChartData'
import { type ChartItem, hasValidChartData } from './services/chartDataService'
import type { AuthoredChartConfig, ChartDataConfig, CopPerformanceChartProps, ResolvedChart } from './types'
import { buildBarChartOptions, buildDonutChartOptions, getDonutCenterText } from './utils/chartOptionBuilders'
import { type ChartVariant, normalizeText, resolveChartVariant } from './utils/variantMapper'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend)

function parseChartsConfig(rawConfig?: string | AuthoredChartConfig[]): AuthoredChartConfig[] {
  if (!rawConfig) {
    return []
  }

  if (Array.isArray(rawConfig)) {
    return rawConfig
  }

  try {
    const parsedConfig = JSON.parse(rawConfig)
    return Array.isArray(parsedConfig) ? parsedConfig : []
  } catch {
    return []
  }
}

function getChartType(chartType?: string): 'donut' | 'bar' | null {
  const normalizedType = normalizeText(chartType)

  if (normalizedType === CHART_TYPE.DONUT) {
    return CHART_TYPE.DONUT
  }

  if (normalizedType === CHART_TYPE.BAR || normalizedType === CHART_TYPE.BAR_CHART) {
    return CHART_TYPE.BAR
  }

  return null
}

function getChartDataConfig(params: {
  chartLabel: string
  explicitVariant?: string
  data?: ChartItem[]
  dataKey?: string
}): ChartDataConfig {
  const { chartLabel, explicitVariant, data, dataKey } = params
  const variant = resolveChartVariant(explicitVariant, chartLabel, dataKey)
  const items = data || []
  const colors = getColorsForData(variant, items)

  return { data: items, colors, variant }
}

function getResolvedCharts(authoredCharts: AuthoredChartConfig[], chartDataByVariant: Record<string, ChartItem[]>): ResolvedChart[] {
  return authoredCharts
    .slice(0, LAYOUT_CONFIG.maxCharts)
    .map(chart => {
      const chartType = getChartType(chart.chartType)
      const chartLabel = chart.chartLabel?.trim() || ''
      const dataKey = chart.dataKey?.trim() || ''

      if (!chartType || !chartLabel) {
        return null
      }

      const variant = resolveChartVariant(chart.variant, chartLabel, dataKey)
      const apiData = chartDataByVariant[variant]
      const chartDataConfig = getChartDataConfig({
        chartLabel,
        explicitVariant: chart.variant,
        data: apiData,
        dataKey,
      })

      if (!hasValidChartData(chartDataConfig.data)) {
        return null
      }

      return {
        chartType,
        chartLabel,
        data: chartDataConfig.data,
        colors: chartDataConfig.colors,
        variant: chartDataConfig.variant,
      }
    })
    .filter((chart): chart is ResolvedChart => chart !== null)
}

function createDoughnutCenterTextPlugin(centerText: string): Plugin<'doughnut'> {
  return {
    id: 'doughnutCenterText',
    afterDatasetsDraw: chart => {
      const firstArc = chart.getDatasetMeta(0).data[0] as { x: number; y: number } | undefined

      if (!firstArc) {
        return
      }

      const { ctx } = chart

      ctx.save()
      ctx.font = `700 12px hitachisans-custom, "Hitachi Sans", sans-serif`
      ctx.fillStyle = COLORS.text
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(centerText, firstArc.x, firstArc.y)
      ctx.restore()
    },
  }
}

function renderDonutChart(chart: ResolvedChart) {
  const donutData = {
    labels: chart.data.map(item => item.name),
    datasets: [
      {
        label: chart.chartLabel,
        data: chart.data.map(item => item.value),
        backgroundColor: chart.colors,
        borderWidth: DONUT_CHART_CONFIG.borderWidth,
        borderColor: COLORS.border.donut,
      },
    ],
  }

  const centerText = getDonutCenterText(chart.data)

  return (
    <div className={CSS_CLASSES.DONUT}>
      <div className={CSS_CLASSES.CHART_TITLE}>{chart.chartLabel}</div>

      <div className={`${CSS_CLASSES.CHART_BODY} ${CSS_CLASSES.CHART_BODY_DONUT}`}>
        <Doughnut data={donutData} options={buildDonutChartOptions()} plugins={[createDoughnutCenterTextPlugin(centerText)]} />
      </div>
    </div>
  )
}

function renderBarChart(chart: ResolvedChart) {
  const barData = {
    labels: chart.data.map(item => item.name),
    datasets: [
      {
        label: chart.chartLabel,
        data: chart.data.map(item => item.value),
        backgroundColor: chart.colors,
        borderWidth: 0,
        minBarLength: 2,
      },
    ],
  }

  return (
    <div className={CSS_CLASSES.BAR}>
      <div className={CSS_CLASSES.CHART_TITLE}>{chart.chartLabel}</div>

      <div className={`${CSS_CLASSES.CHART_BODY} ${CSS_CLASSES.CHART_BODY_BAR}`}>
        <Bar data={barData} options={buildBarChartOptions(chart.data)} />
      </div>
    </div>
  )
}

export default function CopPerformanceChart(props: Readonly<CopPerformanceChartProps>) {
  const authoredCharts = parseChartsConfig(props.chartsjson || props.chartsJson)

  const uniqueVariants = Array.from(
    new Set(
      authoredCharts.map(
        chart =>
          resolveChartVariant(chart.variant, chart.chartLabel?.trim() || '', chart.dataKey?.trim() || '') as ChartVariant,
      ),
    ),
  )

  const chartDataByVariant = useMultipleChartData(uniqueVariants)

  const dataMap = Object.entries(chartDataByVariant).reduce(
    (acc, [variant, result]) => {
      acc[variant] = result.data
      return acc
    },
    {} as Record<string, ChartItem[]>,
  )

  const resolvedCharts = getResolvedCharts(authoredCharts, dataMap)

  if (!resolvedCharts.length) {
    return null
  }

  return (
    <div className={CSS_CLASSES.CHARTS_ROW}>
      {resolvedCharts.map((chart, index) => (
        <div className={CSS_CLASSES.CHART} key={`${chart.chartLabel}-${chart.chartType}-${index}`}>
          {chart.chartType === CHART_TYPE.DONUT ? renderDonutChart(chart) : renderBarChart(chart)}
        </div>
      ))}
    </div>
  )
}
